/**
 * Sheet — `Oprappo Design System.dc.html § 04 ওভারলে · বটম শিট`.
 *
 * Frame note: "Sheets for choosing, modals for confirming, drawers for navigating. A
 * sheet is dismissible by swipe and by tapping outside — nothing important is ever only
 * in a sheet."
 *
 * Panel `background:#fff; box-shadow:0 -2px 14px rgba(14,58,69,.14)` (shadow-sheet).
 * Grabber `padding:13px 15px 0`, bar `36 × 3` in #E2DED5.
 * Body `padding:14px 15px 15px`; title `600 19px/1.75` ink; options start at
 * `margin-top:13px`, each `padding:13px 0` over a `border-top:1px solid #E2DED5`,
 * label `500 15px/1.75` ink; radio 18px, `2px` ring, teal when selected with a 9px
 * teal dot, `#E2DED5` when not. Confirm button `margin-top:15px`.
 *
 * NO SCRIM COLOUR IS SPECIFIED anywhere in the design system. This renders the panel
 * only; the host screen supplies positioning and any backdrop. See README § deviations.
 */
import type { ReactNode } from 'react';
import { Button } from './Button.js';
import { cx } from './internal/cx.js';

export interface SheetOption {
  id: string;
  /** Bangla label, e.g. `অপ্রাপ্য ক্যাশ · ৳ 1,240.50`. */
  labelBn: string;
}

export interface SheetProps {
  /** Bangla sheet title, e.g. `পেমেন্ট মেথড`. */
  titleBn: string;
  options?: readonly SheetOption[] | undefined;
  selectedId?: string | undefined;
  /** Bangla confirm label, e.g. `নিশ্চিত করুন`. Omit to render no action. */
  confirmLabelBn?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
  onConfirm?: (() => void) | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
}

function Radio({ selected }: { selected: boolean }): JSX.Element {
  return (
    <div
      className={cx(
        'w-[18px] h-[18px] border-2 rounded-full flex items-center justify-center flex-none',
        selected ? 'border-teal' : 'border-border',
      )}
    >
      {selected ? <div className="w-[9px] h-[9px] bg-teal rounded-full" /> : null}
    </div>
  );
}

export function Sheet({
  titleBn,
  options,
  selectedId,
  confirmLabelBn,
  onSelect,
  onConfirm,
  children,
  className,
}: SheetProps): JSX.Element {
  return (
    <div role="dialog" aria-label={titleBn} className={cx('bg-white shadow-sheet', className)}>
      <div className="pt-[13px] px-[15px] flex justify-center">
        <div className="w-[36px] h-[3px] bg-border" />
      </div>
      <div className="px-[15px] pt-[14px] pb-[15px]">
        <div className="font-anek font-semibold text-[19px] leading-[1.75] text-ink">{titleBn}</div>

        {options !== undefined && options.length > 0 ? (
          <div className="mt-[13px] flex flex-col">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onSelect?.(o.id)}
                className="py-[13px] border-t border-border flex justify-between items-center bg-transparent px-0 rounded-none text-left"
              >
                <span className="font-anek font-medium text-[15px] leading-[1.75] text-ink">
                  {o.labelBn}
                </span>
                <Radio selected={o.id === selectedId} />
              </button>
            ))}
          </div>
        ) : null}

        {children}

        {confirmLabelBn !== undefined ? (
          <div className="mt-[15px]">
            <Button tone="primary" size="lg" onClick={onConfirm}>
              {confirmLabelBn}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
